class PageNavigator extends HTMLElement {
    constructor() {
        super();
        this.targetSections = [];
        this.navLinks = {};
        this.currentSectionId = null;
        this.sectionObserver = null;
        this.navObserver = null;

        this.isScrolling = false;
        this.scrollTimeout = null;
    }

    connectedCallback() {
        requestAnimationFrame(() => {
            this.initialSetup();
        });
    }

    initialSetup() {
        this.findTargetSections();

        this.backgroundBlob = this.querySelector('.background-blob');
        this.activeTextClass = this.dataset.activeTextClass;

        this.addEventListener('click', this.handleClick.bind(this));

        this.createNavObserver();
        this.createSectionObserver();

        this.checkInitialPositions();
    }

    disconnectedCallback() {
        if (this.navObserver) {
            this.navObserver.disconnect();
        }
        if (this.sectionObserver) {
            this.sectionObserver.disconnect();
        }
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
    }

    findTargetSections() {
        this.targetSections = [];
        this.navLinks = {};

        const links = this.querySelectorAll('a[href^="#"]');

        links.forEach((link) => {
            const href = link.getAttribute('href');
            if (href && href.length > 1) {
                try {
                    const sectionId = decodeURIComponent(href.substring(1));
                    const section = document.getElementById(sectionId);
                    if (section) {
                        this.targetSections.push(section);
                        this.navLinks[sectionId] = link;
                    } else {
                        console.warn(`PageNavigator: Section with ID "${sectionId}" not found.`);
                    }
                } catch (e) {
                    console.error(`PageNavigator: Invalid selector for href="${href}"`, e);
                }
            }
        });
    }

    handleClick(evt) {
        const target = evt.target.closest('a[href]');

        if (target) {
            const targetId = target.getAttribute('href').substring(1);

            const targetElement = document.getElementById(targetId);

            evt.preventDefault();

            const headerOffset = 129;

            const targetPosition =
                targetElement.getBoundingClientRect().top + window.scrollY - headerOffset;

            window.scrollTo({
                top: targetPosition,

                behavior: 'smooth'
            });
        }
    }

    createNavObserver() {
        let observerTarget = this.previousElementSibling;
        if (!observerTarget) {
            observerTarget = this.parentElement;
            console.warn(
                "PageNavigator: No element found before the navigator. Observing parent for sticky behavior, which might be less precise. Consider adding a <div class='nav-marker'> before the navigator."
            );
        }
        if (!observerTarget) {
            console.error('PageNavigator: Cannot determine element to observe for sticky behavior.');
            return;
        }

        this.navObserver = new IntersectionObserver(this.handleNavIntersection.bind(this), {
            root: null,
            rootMargin: `0px 0px 0px 0px`,
            threshold: 0
        });

        this.navObserver.observe(observerTarget);
    }

    createSectionObserver() {
        if (this.targetSections.length === 0) return;

        const headerOffset = this.getBoundingClientRect().height;

        const options = {
            root: null,

            rootMargin: `${headerOffset - 10}px 0px -80% 0px`,
            threshold: 0
        };

        this.sectionObserver = new IntersectionObserver(
            this.handleSectionIntersection.bind(this),
            options
        );
        this.targetSections.forEach((section) => {
            if (section) {
                this.sectionObserver.observe(section);
            }
        });
    }

    handleNavIntersection(entries) {
        entries.forEach((entry) => {
            const rect = entry.boundingClientRect;

            const isBelowViewport = rect.top >= window.innerHeight;

            const show = !isBelowViewport && !entry.isIntersecting;

            if (show) {
                this.classList.add('scrolled-past--animate');

                this.classList.add('scrolled-past');
            } else {
                this.classList.remove('scrolled-past');
            }
        });
    }

    handleSectionIntersection(entries) {
        const currentSection = entries.find((entry) => entry.isIntersecting);

        this.currentSectionId = currentSection?.target.id;

        this.updateCurrentSectionDisplay();
    }

    updateCurrentSectionDisplay() {
        const target = this.navLinks[this.currentSectionId];
        //this.currentSectionId
        if (target && this.activeTextClass) {
            target.classList.remove(this.activeTextClass);
        }

        if (target && this.backgroundBlob) {
            if (this.activeTextClass) {
                target.classList.add(this.activeTextClass);
            }

            const targetRect = target.getBoundingClientRect();
            const parentRect = target.offsetParent.getBoundingClientRect();

            this.backgroundBlob.style.width = `${targetRect.width}px`;
            this.backgroundBlob.style.left = `${targetRect.left - parentRect.left}px`;
        }
    }

    checkInitialPositions() {
        const navTarget = this.previousElementSibling || this.parentElement;
        if (navTarget && navTarget.getBoundingClientRect().bottom < 0) {
            this.classList.add('scrolled-past');
            this.classList.add('scrolled-past--animate');
        }
    }
}

customElements.define('page-navigator', PageNavigator);
